import { formatDistanceToNow, format } from 'date-fns';
import { UserModel, Conversation, Message } from '../types';

// Enhanced last seen formatting
export const formatLastSeen = (lastSeen: Date): string => {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - lastSeen.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  return `${Math.floor(diffInMinutes / 1440)}d ago`;
};

// Enhanced timestamp formatting
export const formatRelativeTime = (timestamp: any): string => {
  try {
    if (!timestamp) return 'Just now';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    return 'Just now';
  }
};

// Enhanced timestamp formatting for message bubbles
export const formatMessageTime = (timestamp: any): string => {
  try {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString() === date.toDateString();
    
    if (isToday) {
      return format(date, 'HH:mm');
    } else if (isYesterday) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM d');
    }
  } catch (error) {
    return '';
  }
};

// Enhanced user display name with fallbacks
export const getUserDisplayName = (user: UserModel | null | undefined): string => {
  if (!user) return 'Unknown User';
  
  try {
    // Try firstName + lastName first
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    
    // Try displayName
    if (user.displayName && user.displayName.trim()) {
      return user.displayName.trim();
    }
    
    // Try firstName only
    if (user.firstName) {
      return user.firstName;
    }
    
    // Try lastName only
    if (user.lastName) {
      return user.lastName;
    }
    
    // Fallback to email username
    if (user.email) {
      const emailName = user.email.split('@')[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    
    return 'Unknown User';
  } catch (error) {
    return 'Unknown User';
  }
};

// Enhanced user initials generation
export const getUserInitials = (user: UserModel | null | undefined): string => {
  if (!user) return '?';
  const name = (user.displayName || `${user.firstName ?? ''} ${user.lastName ?? ''}` || user.email || '').trim();
  const parts = name.split(/\s+/);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
};

// Enhanced profile picture handling
export const getUserProfilePicture = (user: UserModel | null | undefined): string => {
  if (!user) return '';
  try {
    return user.photoURL?.trim() || '';
  } catch (error) {
    return '';
  }
};

// Enhanced unread count calculation
export const getUnreadCount = (conversation: Conversation, currentUserId: string): number => {
  if (!conversation || !currentUserId) return 0;
  
  try {
    // Use the unreadCounts map if available
    if (conversation.unreadCounts && conversation.unreadCounts[currentUserId]) {
      return conversation.unreadCounts[currentUserId];
    }
    
    // Fallback to the legacy unreadCount field
    if (conversation.unreadCount) {
      return conversation.unreadCount;
    }
    
    return 0;
  } catch (error) {
    return 0;
  }
};

// Enhanced conversation blocking check
export const isConversationBlocked = (conversation: Conversation, currentUserId: string): boolean => {
  if (!conversation || !currentUserId) return false;
  
  try {
    return conversation.blockedBy?.includes(currentUserId) || false;
  } catch (error) {
    return false;
  }
};

// Enhanced conversation muting check
export const isConversationMuted = (conversation: Conversation, currentUserId: string): boolean => {
  if (!conversation || !currentUserId) return false;
  
  try {
    return conversation.mutedBy?.includes(currentUserId) || false;
  } catch (error) {
    return false;
  }
};

// Enhanced message preview formatting
export const formatMessagePreview = (message: string, maxLength: number = 40): string => {
  if (!message) return '';
  
  try {
    const trimmed = message.trim();
    if (trimmed.length <= maxLength) {
      return trimmed;
    }
    
    return trimmed.substring(0, maxLength) + '...';
  } catch (error) {
    return message || '';
  }
};

// Enhanced message type detection
export const getMessageType = (message: Message): 'text' | 'image' | 'file' => {
  if (message.imageUrl) return 'image';
  if (message.fileUrl) return 'file';
  return 'text';
};

// Enhanced message preview text
export const getMessagePreviewText = (message: Message): string => {
  const type = getMessageType(message);
  
  switch (type) {
    case 'image':
      return 'Photo';
    case 'file':
      return 'File';
    default:
      return formatMessagePreview(message.text);
  }
};

// Enhanced conversation sorting
export const sortConversations = (conversations: Conversation[]): Conversation[] => {
  try {
    return [...conversations].sort((a, b) => {
      // Sort by unread count first (unread conversations first)
      const aUnread = a.unreadCount || 0;
      const bUnread = b.unreadCount || 0;
      
      if (aUnread > 0 && bUnread === 0) return -1;
      if (aUnread === 0 && bUnread > 0) return 1;
      
      // Then sort by last message time (most recent first)
      const aTime = a.lastMessageTime?.toMillis?.() || a.updatedAt?.toMillis?.() || 0;
      const bTime = b.lastMessageTime?.toMillis?.() || b.updatedAt?.toMillis?.() || 0;
      
      return bTime - aTime;
    });
  } catch (error) {
    return conversations;
  }
};

// Enhanced total unread count calculation
export const getTotalUnreadCount = (conversations: Conversation[], currentUserId: string): number => {
  try {
    return conversations.reduce((total, conversation) => {
      return total + getUnreadCount(conversation, currentUserId);
    }, 0);
  } catch (error) {
    return 0;
  }
};

// Enhanced message validation
export const isValidMessage = (message: Message): boolean => {
  try {
    return !!(
      message.id &&
      message.senderId &&
      (message.text || message.imageUrl || message.fileUrl) &&
      message.timestamp
    );
  } catch (error) {
    return false;
  }
};

// Enhanced conversation validation
export const isValidConversation = (conversation: Conversation): boolean => {
  try {
    return !!(
      conversation.id &&
      conversation.participants &&
      Array.isArray(conversation.participants) &&
      conversation.participants.length >= 2
    );
  } catch (error) {
    return false;
  }
};

// Enhanced user validation
export const isValidUser = (user: UserModel): boolean => {
  try {
    return !!(
      user &&
      user.uid &&
      (user.firstName || user.lastName || user.displayName || user.email)
    );
  } catch (error) {
    return false;
  }
}; 